import React, { useEffect, useMemo, useState } from 'react';
import { Box, costFormater, Option, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { PERMISSIONS, ROUTES } from '@/common';
import { ALL_OPTION, DEFAULT_DATE_FORMAT, DEFAULT_DATE_TIME_FORMAT } from '@/common/constants';
import { ValuesMap } from '@/common/types';
import { useHasAccess } from '@/modules/core';
import Link from '@/modules/core/components/Link';

import {
  ContentResponse,
  ReportColumnType,
  ReportHeaderResponse,
  ReportPlansResponse,
} from '../../report.types';
export interface Params {
  isLoading: boolean;
  contentList: ContentResponse[];
  columnsSize: ValuesMap<number>;
  rawHeaders: ReportHeaderResponse[];
  rawPlans: ReportPlansResponse[];
  lastUpdatedAt?: string;
  isSSNShowed?: boolean;
  isPreviewMode?: boolean;
}

const EMPLOYEE_ID_KEY = 'employee_id';
const LINK_EMPLOYEE_KEY = 'last_name';
const SSN_KEY = 'ssn';
export const DEFAULT_COLUMN_WIDTH = 100;

const MASK_SSN_SIGHT = '*';

const PREVIEW_LAST_DIGITS = 4;
const LAST_DIGITS = 4;
export const COLUMN_SIZE_MAP: ValuesMap<number> = {
  partner_id: 88,
  partner_name: 150,
  distributor_id: 120,
  distributor_external_identifier: 120,
  employer_external_identifier: 120,
  employer_id: 130,
  employer_name: 140,
  subsidiary_id: 110,
  subsidiary_name: 140,
  first_name: 100,
  invoice_id: 150,
  last_name: 100,
  employer_employee_id: 180,
  partner_employee_id: 190,
  ssn: 92,
  primary_id_source: 120,
  transaction_date: 150,
  employee_contribution_amount: 230,
  employer_contribution_amount: 230,
  plan_name: 180,
  plan_year: 180,
  plan_year_valid_from: 150,
  plan_year_valid_to: 150,
  plan_code: 110,
  tax_year: 90,
  contribution_date: 140,
  memo: 100,
  account_type: 120,
  account_balance: 135,

  service_type: 140,
  claim_status: 140,
  employment_status: 150,
  claim_submit_date: 145,
  denial_reason: 120,
  requested_amount: 150,
  approved_amount: 150,
  acct_paid_amount: 150,
  total_paid_amount: 150,
  payment_status: 130,
  paid_date: 100,
  payment_method: 140,

  enrollment_effective_date: 190,
  employment_status_date: 190,
  employer_contributions: 175,
  employee_contributions: 175,
  enrollment_amount: 150,
  claim_spend: 110,
  custom_reporting_field_1_name: 200,
  custom_reporting_field_1: 200,
  custom_reporting_field_2_name: 200,
  custom_reporting_field_2: 200,
  custom_reporting_field_3_name: 200,
  custom_reporting_field_3: 200,
  custom_reporting_field_4_name: 200,
  custom_reporting_field_4: 200,
  custom_reporting_field_5_name: 200,
  custom_reporting_field_5: 200,
};

const useReportTableBase = ({
  isLoading,
  contentList,
  columnsSize,
  rawHeaders,
  rawPlans,
  lastUpdatedAt,
  isSSNShowed,
  isPreviewMode,
}: Params) => {
  const isHasProfilePermission = useHasAccess([{ permission: PERMISSIONS.VIEW_PROFILE }]);
  const [headerKeys, headerLabels, headerTypes] = useMemo(() => {
    const list = rawHeaders;
    const sortedList = _.sortBy(list, (o) => o.position);
    const keys = sortedList.map((item) => item.field_name);
    const labels = sortedList.map((item) => item.column_label);
    const headerTypes = sortedList.map((item) => item.column_type);
    return [keys, labels, headerTypes];
  }, [rawHeaders]);
  const headers = useMemo(() => {
    if (isLoading && (!contentList.length || !headerKeys.length)) {
      return Object.keys(columnsSize).map((key) => (
        <Box pad={{ horizontal: 'spacing8' }} key={key}>
          <Text color="textSecondary" truncate>
            _
          </Text>
        </Box>
      ));
    }

    return headerKeys.map((item, index) => (
      <Box height="25px" justify="center" pad={{ horizontal: 'spacing8' }} key={item}>
        <Text
          color="textSecondary"
          data-testid={`report-table_${index}`}
          truncate
          size="small"
          weight={500}
          title={headerLabels[index]}
        >
          {headerLabels[index]}
        </Text>
      </Box>
    ));
  }, [isLoading, contentList.length, headerKeys, columnsSize, headerLabels]);

  const formattedContentList = useMemo(() => {
    return (
      contentList.map((item) => {
        const ssn = `${item.ssn || ''}`;
        if (!ssn || isSSNShowed) {
          return item;
        }
        const lastDigits = isPreviewMode ? PREVIEW_LAST_DIGITS : LAST_DIGITS;
        const lastFour = `${ssn}`.slice(-1 * lastDigits);
        const maskedPart = MASK_SSN_SIGHT.repeat(5);
        const transformedString = `${maskedPart}${lastFour}`;

        return {
          ...item,
          ssn: transformedString,
        } as ContentResponse;
      }) || []
    );
  }, [contentList, isPreviewMode, isSSNShowed]);
  const nodes: React.ReactNode[][] = useMemo(() => {
    const list = formattedContentList;

    return list.map((item, index) => {
      return headerKeys.map((headerKey, headerIndex) => {
        if (headerKey === LINK_EMPLOYEE_KEY && item[EMPLOYEE_ID_KEY] && isHasProfilePermission) {
          return (
            <Box pad={{ horizontal: 'spacing8' }} key={`${index} ${headerKey}`}>
              <Link isOpenNewTab to={ROUTES.EMPLOYEE_VIEW((item[EMPLOYEE_ID_KEY] as string) || '')}>
                <Text
                  color="textAccent"
                  size="small"
                  data-testid={`report-table_${index}_${headerKey}`}
                  truncate
                  title={`${item[headerKey] || ''}`}
                  weight={500}
                >
                  {item[headerKey]}
                </Text>
              </Link>
            </Box>
          );
        }
        let value = item[headerKey];
        if (headerTypes[headerIndex] === ReportColumnType.Money) {
          if (value) {
            value = costFormater(_.toNumber(value), {
              isPrecision: true,
            });
          } else {
            value = '';
          }
        }
        if (headerTypes[headerIndex] === ReportColumnType.Date) {
          value = value ? dayjs(value as string).format(DEFAULT_DATE_FORMAT) : '';
        }
        if (headerTypes[headerIndex] === ReportColumnType.DateTime) {
          value = value ? dayjs(value as string).format(DEFAULT_DATE_TIME_FORMAT) : '';
        }

        const SSNKey = headerKey === SSN_KEY ? `${isSSNShowed}` : null;

        return (
          <Box pad={{ horizontal: 'spacing8' }} key={`${index} ${headerKey}`}>
            <Text
              data-testid={`report-table_${index}_${headerKey}`}
              key={SSNKey}
              size="small"
              truncate
              title={`${value}`}
              weight={500}
            >
              {value}
            </Text>
          </Box>
        );
      });
    });
  }, [formattedContentList, headerKeys, headerTypes, isSSNShowed, isHasProfilePermission]);

  const columnWidths = useMemo(() => {
    return headerKeys.map((item) => columnsSize[item] || COLUMN_SIZE_MAP[item] || DEFAULT_COLUMN_WIDTH);
  }, [columnsSize, headerKeys]);

  const [savedPlans, setSavedPlans] = useState<Option[]>([]);

  useEffect(() => {
    const list = rawPlans || [];
    if (!savedPlans.length && list.length) {
      const options: Option[] = list.map((item) => ({
        key: `${item.id}`,
        value: item.name,
        title: item.name,
      }));
      setSavedPlans(options);
    }
  }, [rawPlans, savedPlans.length]);
  const planOptions = useMemo<Option[]>(() => {
    return [ALL_OPTION, ...savedPlans];
  }, [savedPlans]);

  const lastTimeUpdateDate = useMemo(() => {
    const lastTime = lastUpdatedAt;
    if (lastTime && dayjs(lastTime).isValid()) {
      return dayjs(lastTime);
    }
    return null;
  }, [lastUpdatedAt]);
  return {
    headerKeys,
    onChangePlanOptions: setSavedPlans,
    planOptions,
    lastTimeUpdateDate,
    headers,
    nodes,
    columnWidths,
  };
};

export default useReportTableBase;
